export type ThemeTextRole = 'header' | 'bold' | 'normal' | 'descriptive' | 'other';
export type ThemeColorRole = 'primary' | 'accent' | 'info' | 'success' | 'warning' | 'danger';
export type ThemeFontId = 'inter' | 'google-sans' | 'helvetica' | 'arial';

export const TEXT_ROLES: ThemeTextRole[] = ['header', 'bold', 'normal', 'descriptive', 'other'];
export const COLOR_ROLES: ThemeColorRole[] = ['primary', 'accent', 'info', 'success', 'warning', 'danger'];

export const TYPE_SCALE_MIN = 0.75;
export const TYPE_SCALE_MAX = 1.5;
export const TYPE_SCALE_STEP = 0.05;

const STORAGE_KEY = 'mockloc-appearance';

export interface Appearance {
  font: ThemeFontId;
  typeScale: Record<ThemeTextRole, number>;
  colors: Record<ThemeColorRole, string | null>;
}

export const BUILTIN_COLORS: Record<ThemeColorRole, { dark: string; light: string }> = {
  primary: { dark: '#7c5cff', light: '#6d4dff' },
  accent: { dark: '#ffde03', light: '#ffde03' },
  info: { dark: '#60a5fa', light: '#2563eb' },
  success: { dark: '#34d399', light: '#059669' },
  warning: { dark: '#fbbf24', light: '#b45309' },
  danger: { dark: '#fb7185', light: '#e11d48' },
};

const FONT_STACK: Record<ThemeFontId, string> = {
  inter: "'Inter', system-ui, sans-serif",
  'google-sans': "'MockLoc Google Sans', 'Inter', system-ui, sans-serif",
  helvetica: "Helvetica, 'Helvetica Neue', Arial, sans-serif",
  arial: 'Arial, Helvetica, sans-serif',
};

export const FONT_OPTIONS: ReadonlyArray<{ id: ThemeFontId; label: string; note: string }> = [
  {
    id: 'inter',
    label: 'Inter',
    note: 'Font the site uses today. Regular through Black are loaded.',
  },
  {
    id: 'google-sans',
    label: 'Google Sans',
    note: 'Regular file covers 400 and 500. Bold file covers 600 through 900.',
  },
  {
    id: 'helvetica',
    label: 'Helvetica',
    note: 'Uses Helvetica on this device, then Helvetica Neue, then Arial.',
  },
  {
    id: 'arial',
    label: 'Arial',
    note: 'Regular and Bold are installed faces. Other weights are synthesized.',
  },
];

const MANAGED_PROPS = [
  '--ml-font-family',
  '--ml-font-display',
  '--ml-type-header',
  '--ml-type-bold',
  '--ml-type-normal',
  '--ml-type-descriptive',
  '--ml-type-other',
  '--ml-primary',
  '--ml-primary-rgb',
  '--ml-primary-soft',
  '--ml-primary-soft-rgb',
  '--ml-primary-deep',
  '--ml-primary-deep-rgb',
  '--ml-primary-wash',
  '--ml-primary-wash-rgb',
  '--ml-accent',
  '--ml-accent-rgb',
  '--ml-accent-soft',
  '--ml-accent-soft-rgb',
  '--ml-info',
  '--ml-info-rgb',
  '--ml-success',
  '--ml-success-rgb',
  '--ml-warning',
  '--ml-warning-rgb',
  '--ml-danger',
  '--ml-danger-rgb',
];

export function defaultAppearance(): Appearance {
  return {
    font: 'inter',
    typeScale: {
      header: 1,
      bold: 1,
      normal: 1,
      descriptive: 1,
      other: 1,
    },
    colors: {
      primary: null,
      accent: null,
      info: null,
      success: null,
      warning: null,
      danger: null,
    },
  };
}

export function fontStack(id: ThemeFontId): string {
  return FONT_STACK[id];
}

export function clampScale(value: number): number {
  if (!Number.isFinite(value)) return 1;
  const percent = Math.round(value * 100);
  const stepped = Math.round(percent / (TYPE_SCALE_STEP * 100)) * (TYPE_SCALE_STEP * 100);
  const bounded = Math.min(TYPE_SCALE_MAX * 100, Math.max(TYPE_SCALE_MIN * 100, stepped));
  return bounded / 100;
}

export function parseHex(input: string): string | null {
  const match = /^#([0-9a-fA-F]{6})$/.exec(input.trim());
  if (!match) return null;
  return `#${match[1].toLowerCase()}`;
}

export function mixHex(source: string, target: string, targetWeight: number): string {
  const from = parseHex(source);
  const to = parseHex(target);
  if (!from || !to) return source;
  const weight = Math.min(1, Math.max(0, targetWeight));
  const channels = [1, 3, 5].map((index) => {
    const sourceChannel = parseInt(from.slice(index, index + 2), 16);
    const targetChannel = parseInt(to.slice(index, index + 2), 16);
    return Math.round(sourceChannel * (1 - weight) + targetChannel * weight);
  });
  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function hexChannels(hex: string): string {
  const parsed = parseHex(hex);
  if (!parsed) return '0, 0, 0';
  const channels = [1, 3, 5].map((index) => parseInt(parsed.slice(index, index + 2), 16));
  return channels.join(', ');
}

function readFont(value: unknown): ThemeFontId {
  if (value === 'inter' || value === 'google-sans' || value === 'helvetica' || value === 'arial') {
    return value;
  }
  return 'inter';
}

export function sanitizeAppearance(value: unknown): Appearance {
  const base = defaultAppearance();
  if (!value || typeof value !== 'object') return base;
  const record = value as Partial<Appearance>;
  base.font = readFont(record.font);
  if (record.typeScale && typeof record.typeScale === 'object') {
    TEXT_ROLES.forEach((role) => {
      const scale = record.typeScale?.[role];
      if (typeof scale === 'number') base.typeScale[role] = clampScale(scale);
    });
  }
  if (record.colors && typeof record.colors === 'object') {
    COLOR_ROLES.forEach((role) => {
      const color = record.colors?.[role];
      base.colors[role] = typeof color === 'string' ? parseHex(color) : null;
    });
  }
  return base;
}

export function readStoredAppearance(): Appearance {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultAppearance();
    return sanitizeAppearance(JSON.parse(stored));
  } catch {
    return defaultAppearance();
  }
}

export function storeAppearance(appearance: Appearance): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    /* Ignore storage failures. The in-memory choice still applies. */
  }
}

export function applyAppearance(appearance: Appearance): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  MANAGED_PROPS.forEach((name) => root.style.removeProperty(name));

  TEXT_ROLES.forEach((role) => {
    const scale = appearance.typeScale[role];
    if (scale !== 1) root.style.setProperty(`--ml-type-${role}`, String(scale));
  });

  if (appearance.font !== 'inter') {
    const stack = FONT_STACK[appearance.font];
    root.style.setProperty('--ml-font-family', stack);
    root.style.setProperty('--ml-font-display', stack);
  }

  COLOR_ROLES.forEach((role) => {
    const hex = appearance.colors[role];
    if (!hex) return;
    root.style.setProperty(`--ml-${role}`, hex);
    root.style.setProperty(`--ml-${role}-rgb`, hexChannels(hex));
    if (role === 'primary' || role === 'accent') {
      const soft = mixHex(hex, '#ffffff', 0.55);
      root.style.setProperty(`--ml-${role}-soft`, soft);
      root.style.setProperty(`--ml-${role}-soft-rgb`, hexChannels(soft));
    }
    if (role === 'primary') {
      const deep = mixHex(hex, '#140b2e', 0.45);
      const wash = mixHex(hex, '#ffffff', 0.88);
      root.style.setProperty('--ml-primary-deep', deep);
      root.style.setProperty('--ml-primary-deep-rgb', hexChannels(deep));
      root.style.setProperty('--ml-primary-wash', wash);
      root.style.setProperty('--ml-primary-wash-rgb', hexChannels(wash));
    }
  });
}

/** Read a user color for canvases that cannot inherit CSS variables, such as map images. */
export function readAppliedHex(variable: string, fallback: string): string {
  try {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
    return parseHex(value) ?? fallback;
  } catch {
    return fallback;
  }
}
